import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Gavel as AuctionIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Schedule as ScheduleIcon
} from '@mui/icons-material';
import { updateAuctionDetails, updateAuctionOutcome } from '../services/propertyService';
import type { Property } from '../types/property';

interface AuctionSchedulingProps {
  property: Property;
  agencySlug: string;
  onUpdate: () => void;
  defaultToLastOpening?: boolean;
  lastOpeningDate?: string;
  lastOpeningTime?: string;
}

interface AuctionDetails {
  date?: string;
  time?: string;
  location?: string;
  reservePrice?: number;
  auctioneer?: string;
  notes?: string;
}

interface AuctionOutcome {
  result?:
    | 'sold-prior-to-auction'
    | 'sold-at-auction'
    | 'passed-in'
    | 'passed-in-vendor-bid'
    | 'withdrawn'
    | 'sold-after-auction';
  date?: string;
  maxBid?: number;
  notes?: string;
}

const AuctionScheduling: React.FC<AuctionSchedulingProps> = ({
  property,
  agencySlug,
  onUpdate,
  defaultToLastOpening = true,
  lastOpeningDate,
  lastOpeningTime
}) => {
  const [auctionDetails, setAuctionDetails] = useState<AuctionDetails>({});
  const [auctionOutcome, setAuctionOutcome] = useState<AuctionOutcome>({});
  const [editMode, setEditMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    // Initialize auction details from property or defaults
    const details = property.auctionDetails || {};
    const outcome = property.auctionOutcome || {};
    
    if (defaultToLastOpening && lastOpeningDate && lastOpeningTime && !details.date && !details.time) {
      // Default to last opening time
      setAuctionDetails({
        ...details,
        date: lastOpeningDate,
        time: lastOpeningTime
      });
    } else {
      setAuctionDetails(details);
    }
    setAuctionOutcome(outcome);
  }, [property.auctionDetails, property.auctionOutcome, defaultToLastOpening, lastOpeningDate, lastOpeningTime]);

  const handleSave = async () => {
    try {
      setLoading(true);
      setError(null);
      
      await updateAuctionDetails(agencySlug, property._id, auctionDetails);
      await updateAuctionOutcome(agencySlug, property._id, auctionOutcome);
      
      setSuccess('Auction details updated successfully!');
      setEditMode(false);
      onUpdate();
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to update auction details');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setAuctionDetails(property.auctionDetails || {});
    setAuctionOutcome(property.auctionOutcome || {});
    setEditMode(false);
    setError(null);
  };

  const formatDateTime = (date?: string, time?: string) => {
    if (!date) return 'Not scheduled';
    
    const dateObj = new Date(date);
    const formattedDate = dateObj.toLocaleDateString('en-AU', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
    
    if (time) {
      const [hours, minutes] = time.split(':');
      const timeObj = new Date();
      timeObj.setHours(parseInt(hours), parseInt(minutes));
      const formattedTime = timeObj.toLocaleTimeString('en-AU', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
      return `${formattedDate} at ${formattedTime}`;
    }
    
    return formattedDate;
  };

  const isAuctionScheduled = auctionDetails.date && auctionDetails.time;

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Box display="flex" alignItems="center" gap={1}>
          <AuctionIcon color="primary" />
          <Typography variant="h6">Auction Scheduling</Typography>
          {isAuctionScheduled && (
            <Chip 
              label="Scheduled" 
              color="success" 
              size="small"
              icon={<ScheduleIcon />}
            />
          )}
        </Box>
        
        {!editMode && (
          <Tooltip title="Edit auction details">
            <IconButton onClick={() => setEditMode(true)}>
              <EditIcon />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {editMode ? (
        <Box>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Auction Date"
                type="date"
                value={auctionDetails.date || ''}
                onChange={(e) => setAuctionDetails({ ...auctionDetails, date: e.target.value })}
                fullWidth
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                label="Auction Time"
                type="time"
                value={auctionDetails.time || ''}
                onChange={(e) => setAuctionDetails({ ...auctionDetails, time: e.target.value })}
                fullWidth
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                label="Auction Location"
                value={auctionDetails.location || ''}
                onChange={(e) => setAuctionDetails({ ...auctionDetails, location: e.target.value })}
                fullWidth
                placeholder="e.g., On-site, Auction House, Online"
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                label="Reserve Price"
                type="number"
                value={auctionDetails.reservePrice || ''}
                onChange={(e) => setAuctionDetails({ 
                  ...auctionDetails, 
                  reservePrice: e.target.value ? parseFloat(e.target.value) : undefined 
                })}
                fullWidth
                InputProps={{
                  startAdornment: <Typography sx={{ mr: 1 }}>$</Typography>
                }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                label="Auctioneer"
                value={auctionDetails.auctioneer || ''}
                onChange={(e) => setAuctionDetails({ ...auctionDetails, auctioneer: e.target.value })}
                fullWidth
                placeholder="Auctioneer name or company"
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                label="Notes"
                value={auctionDetails.notes || ''}
                onChange={(e) => setAuctionDetails({ ...auctionDetails, notes: e.target.value })}
                fullWidth
                multiline
                rows={3}
                placeholder="Additional auction details, special instructions, etc."
              />
            </Grid>
          </Grid>

          {/* Auction Outcome (actual result) */}
          <Box mt={4}>
            <Typography variant="subtitle1" gutterBottom>
              Auction Outcome
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                  <InputLabel id="auction-outcome-result-label">Result</InputLabel>
                  <Select
                    labelId="auction-outcome-result-label"
                    label="Result"
                    value={auctionOutcome.result || ''}
                    onChange={(e) =>
                      setAuctionOutcome({
                        ...auctionOutcome,
                        result: e.target.value as AuctionOutcome['result'],
                      })
                    }
                  >
                    <MenuItem value="">No outcome recorded</MenuItem>
                    <MenuItem value="sold-prior-to-auction">Sold prior to auction</MenuItem>
                    <MenuItem value="sold-at-auction">Sold at auction</MenuItem>
                    <MenuItem value="sold-after-auction">Sold after auction</MenuItem>
                    <MenuItem value="passed-in">Passed in</MenuItem>
                    <MenuItem value="passed-in-vendor-bid">Passed in (vendor bid)</MenuItem>
                    <MenuItem value="withdrawn">Withdrawn</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="Outcome Date"
                  type="date"
                  value={auctionOutcome.date || auctionDetails.date || ''}
                  onChange={(e) =>
                    setAuctionOutcome({
                      ...auctionOutcome,
                      date: e.target.value || undefined,
                    })
                  }
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              {auctionOutcome.result === 'passed-in-vendor-bid' && (
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Maximum Bid"
                    type="number"
                    value={auctionOutcome.maxBid ?? ''}
                    onChange={(e) =>
                      setAuctionOutcome({
                        ...auctionOutcome,
                        maxBid: e.target.value ? parseFloat(e.target.value) : undefined,
                      })
                    }
                    fullWidth
                    InputProps={{
                      startAdornment: <Typography sx={{ mr: 1 }}>$</Typography>,
                    }}
                  />
                </Grid>
              )}

              <Grid item xs={12}>
                <TextField
                  label="Outcome Notes (internal only)"
                  value={auctionOutcome.notes || ''}
                  onChange={(e) =>
                    setAuctionOutcome({
                      ...auctionOutcome,
                      notes: e.target.value,
                    })
                  }
                  fullWidth
                  multiline
                  rows={2}
                  placeholder="Record key auction results, e.g. number of bidders, conditions, etc."
                />
              </Grid>
            </Grid>
          </Box>

          <Box display="flex" gap={2} mt={3}>
            <Button
              variant="contained"
              startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
              onClick={handleSave}
              disabled={loading || !auctionDetails.date || !auctionDetails.time}
            >
              {loading ? 'Saving...' : 'Save Auction Details'}
            </Button>
            
            <Button
              variant="outlined"
              startIcon={<CancelIcon />}
              onClick={handleCancel}
              disabled={loading}
            >
              Cancel
            </Button>
          </Box>
        </Box>
      ) : (
        <Box>
          {isAuctionScheduled ? (
            <Box>
              <Typography variant="body1" gutterBottom>
                <strong>Auction Date & Time:</strong> {formatDateTime(auctionDetails.date, auctionDetails.time)}
              </Typography>
              
              {auctionDetails.location && (
                <Typography variant="body1" gutterBottom>
                  <strong>Location:</strong> {auctionDetails.location}
                </Typography>
              )}
              
              {auctionDetails.reservePrice && (
                <Typography variant="body1" gutterBottom>
                  <strong>Reserve Price:</strong> ${auctionDetails.reservePrice.toLocaleString()}
                </Typography>
              )}
              
              {auctionDetails.auctioneer && (
                <Typography variant="body1" gutterBottom>
                  <strong>Auctioneer:</strong> {auctionDetails.auctioneer}
                </Typography>
              )}
              
              {auctionDetails.notes && (
                <Typography variant="body1" gutterBottom>
                  <strong>Notes:</strong> {auctionDetails.notes}
                </Typography>
              )}
            </Box>
          ) : (
            <Box textAlign="center" py={4}>
              <AuctionIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No Auction Scheduled
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={3}>
                {defaultToLastOpening && lastOpeningDate && lastOpeningTime
                  ? `Default suggestion: ${formatDateTime(lastOpeningDate, lastOpeningTime)}`
                  : 'Schedule the auction date and time to proceed with the auction workflow.'
                }
              </Typography>
              <Button
                variant="contained"
                startIcon={<ScheduleIcon />}
                onClick={() => setEditMode(true)}
              >
                Schedule Auction
              </Button>
            </Box>
          )}
        </Box>
      )}
    </Paper>
  );
};

export default AuctionScheduling;
